import React, { useMemo, useState } from 'react'

import { Button, BUTTON_THEME, Input, Select } from '@st-operations/uikit/src/v2/index.js'
import pretty from '@st-operations/uikit/lib/pretty.js'

import { useDestinations, useRegions } from '_hooks'

import AudienceDiff from '../components/audience-diff'
import { getAudienceIntegrations, INTEGRATION_FIELDS, prettyField } from '../collections'
import { APPEND_SEGMENT, LIST_MODES, NAME_MODES, splitName } from '../lib/operations'

const NAME_MODE_OPTIONS = [
  { value: NAME_MODES.NONE, label: 'No change' },
  { value: NAME_MODES.FIND_REPLACE, label: 'Find & replace text' },
  { value: NAME_MODES.ADD_PREFIX, label: 'Add a prefix' },
  { value: NAME_MODES.SET_SEGMENT, label: 'Set a name segment' },
  { value: NAME_MODES.ADD_SEGMENT, label: 'Add a name segment' },
]

const LIST_MODE_OPTIONS = [
  { value: LIST_MODES.NONE, label: 'No change' },
  { value: LIST_MODES.ADD, label: 'Add to current' },
  { value: LIST_MODES.REMOVE, label: 'Remove from current' },
  { value: LIST_MODES.REPLACE, label: 'Replace all with' },
]

const EditStage = ({
  collection,
  audiences,
  plan,
  operations,
  setOperations,
  schema,
  onBack,
  onContinue,
}) => {
  const { capabilities } = collection
  const regions = useRegions()
  const destinations = useDestinations()
  const [previewId, setPreviewId] = useState(null)

  const affected = useMemo(() => plan.filter(({ changeCount }) => changeCount > 0), [plan])

  // splitName drops empty segments, so a trailing ">" no longer inflates the
  // count into an index the apply step would reject
  const maxNameSegments = useMemo(
    () => Math.max(1, ...audiences.map(({ name }) => splitName(name).length)),
    [audiences]
  )

  const segmentOptions = useMemo(
    () => Array.from({ length: maxNameSegments }, (_, index) => ({
      value: index + 1,
      label: `Segment ${index + 1}`,
    })),
    [maxNameSegments]
  )

  // ADD_SEGMENT can also append after the last segment of each audience
  const beforeSegmentOptions = useMemo(
    () => [...segmentOptions, { value: APPEND_SEGMENT, label: 'At the end' }],
    [segmentOptions]
  )

  // only offer integration settings that exist in the loaded result set
  const integrationCounts = useMemo(() => {
    if (!capabilities.integrationFields) return {}
    const counts = {}
    audiences.forEach((audience) => {
      getAudienceIntegrations(audience).forEach((integration) => {
        counts[integration] = (counts[integration] || 0) + 1
      })
    })
    return counts
  }, [audiences, capabilities.integrationFields])

  const previewItem =
    plan.find(({ audience }) => audience._id === previewId) || affected[0] || plan[0]

  const setNameOperation = (patch) =>
    setOperations((prev) => ({ ...prev, name: { ...prev.name, ...patch } }))

  const setStatusOperation = (option) =>
    setOperations((prev) => ({ ...prev, status: { value: option?.value ?? '' } }))

  const setListOperation = (key) => (patch) =>
    setOperations((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }))

  const setFieldOperation = (field) => (value) =>
    setOperations((prev) => ({
      ...prev,
      fields: { ...prev.fields, [field]: value?.value ?? value ?? '' },
    }))

  const renderIntegrationField = (field) => {
    const fieldSchema = schema?.[field]
    const label = fieldSchema?.label || prettyField(field)
    const value = operations.fields[field] ?? ''

    if (fieldSchema?.enum) {
      return (
        <Select
          key={field}
          label={label}
          useReactSelect
          isClearable
          options={fieldSchema.enum}
          value={value || null}
          onChange={setFieldOperation(field)}
        />
      )
    }

    return (
      <Input
        key={field}
        label={label}
        placeholder="Leave empty to keep current value"
        value={value}
        onChange={setFieldOperation(field)}
      />
    )
  }

  const renderListCard = (key, label, options) => {
    const operation = operations[key]
    return (
      <div className="bulk-edit-operation">
        <Select
          label={label}
          useReactSelect
          options={LIST_MODE_OPTIONS}
          value={LIST_MODE_OPTIONS.find(({ value }) => value === operation.mode)}
          onChange={(option) => setListOperation(key)({ mode: option?.value ?? LIST_MODES.NONE })}
        />
        <If condition={operation.mode !== LIST_MODES.NONE}>
          <Select
            label={`${prettyField(key)} to ${operation.mode}`}
            useReactSelect
            isMulti
            isSearchable
            options={options}
            value={operation.values}
            onChange={(values) => setListOperation(key)({ values: values || [] })}
          />
        </If>
      </div>
    )
  }

  return (
    <div className="bulk-edit-edit">
      <div className="bulk-edit-edit__operations">
        <section className="bulk-edit__card">
          <header className="bulk-edit__card-header">
            <h3>2. Describe the changes — {collection.label}</h3>
          </header>
          <p className="bulk-edit__hint">
            The rules below are applied to all {audiences.length} loaded audiences. Fields left as
            “No change” / empty are not touched.
          </p>

          <div className="bulk-edit-operation">
            <Select
              label="Audience Name"
              useReactSelect
              options={NAME_MODE_OPTIONS}
              value={NAME_MODE_OPTIONS.find(({ value }) => value === operations.name.mode)}
              onChange={(option) => setNameOperation({ mode: option?.value ?? NAME_MODES.NONE })}
            />
            <If condition={operations.name.mode === NAME_MODES.FIND_REPLACE}>
              <div className="bulk-edit-operation__row">
                <Input
                  label="Find"
                  placeholder="e.g. UPDATE_ME"
                  value={operations.name.find}
                  onChange={(value) => setNameOperation({ find: value })}
                />
                <Input
                  label="Replace with"
                  placeholder="leave empty to remove"
                  value={operations.name.replace}
                  onChange={(value) => setNameOperation({ replace: value })}
                />
              </div>
            </If>
            <If condition={operations.name.mode === NAME_MODES.ADD_PREFIX}>
              <div className="bulk-edit-operation__row">
                <Input
                  label="Prefix"
                  placeholder="e.g. [Archived] "
                  value={operations.name.prefix}
                  onChange={(value) => setNameOperation({ prefix: value })}
                />
              </div>
              <p className="bulk-edit__hint">
                The prefix is prepended as-is — no separator is added, so include a trailing space
                (e.g. “[Archived] ”) if you want one. Use “Add a name segment” to prepend a “&gt;”
                separated segment instead.
              </p>
            </If>
            <If condition={operations.name.mode === NAME_MODES.SET_SEGMENT}>
              <div className="bulk-edit-operation__row">
                <Select
                  label="Segment (split by >)"
                  useReactSelect
                  options={segmentOptions}
                  value={segmentOptions.find(
                    ({ value }) => value === operations.name.segment_index
                  )}
                  onChange={(option) => setNameOperation({ segment_index: option?.value ?? 1 })}
                />
                <Input
                  label="New value"
                  placeholder="leave empty to remove the segment"
                  value={operations.name.value}
                  onChange={(value) => setNameOperation({ value })}
                />
              </div>
              <p className="bulk-edit__hint">
                Audiences with fewer segments than the selected one are left untouched.
              </p>
            </If>
            <If condition={operations.name.mode === NAME_MODES.ADD_SEGMENT}>
              <div className="bulk-edit-operation__row">
                <Select
                  label="Position (split by >)"
                  useReactSelect
                  options={beforeSegmentOptions}
                  value={beforeSegmentOptions.find(
                    ({ value }) => value === operations.name.before_segment_index
                  )}
                  onChange={(option) =>
                    setNameOperation({ before_segment_index: option?.value ?? 1 })
                  }
                />
                <Input
                  label="New segment"
                  placeholder="e.g. Archived"
                  value={operations.name.new_segment}
                  onChange={(value) => setNameOperation({ new_segment: value })}
                />
              </div>
              <p className="bulk-edit__hint">
                The new segment is inserted before the selected one, or appended after the last
                segment when “At the end” is chosen. When inserting before a segment, audiences with
                fewer segments than the selected one are left untouched.
              </p>
            </If>
          </div>

          <If condition={capabilities.status}>
            <div className="bulk-edit-operation">
              <Select
                label="Status"
                placeholder="No change"
                useReactSelect
                isClearable
                options={collection.statuses}
                value={operations.status.value || null}
                onChange={setStatusOperation}
              />
            </div>
          </If>

          <If condition={capabilities.regions}>{renderListCard('regions', 'Regions', regions)}</If>
          <If condition={capabilities.destinations}>
            {renderListCard('destinations', 'Destinations', destinations)}
          </If>
        </section>

        <If condition={capabilities.integrationFields}>
          <section className="bulk-edit__card">
            <header className="bulk-edit__card-header">
              <h3>Destination settings</h3>
            </header>
            <Choose>
              <When condition={Object.keys(integrationCounts).length === 0}>
                <p className="bulk-edit__empty">
                  None of the loaded audiences has destination-specific settings.
                </p>
              </When>
              <Otherwise>
                <p className="bulk-edit__hint">
                  Each value is only applied to audiences delivering to that integration.
                </p>
                <For each="integration" of={Object.keys(integrationCounts)}>
                  <div key={integration} className="bulk-edit-operation">
                    <h4 className="bulk-edit-operation__title">
                      {pretty(integration)}{' '}
                      <span className="bulk-edit-operation__count">
                        {integrationCounts[integration]} audience(s)
                      </span>
                    </h4>
                    <div className="bulk-edit-operation__fields">
                      {INTEGRATION_FIELDS[integration].map(renderIntegrationField)}
                    </div>
                  </div>
                </For>
              </Otherwise>
            </Choose>
          </section>
        </If>
      </div>

      <aside className="bulk-edit-edit__preview">
        <section className="bulk-edit__card">
          <header className="bulk-edit__card-header">
            <h3>Live preview</h3>
          </header>
          <p className="bulk-edit__summary">
            <strong>{affected.length}</strong> of <strong>{audiences.length}</strong> audiences will
            be modified.
          </p>
          <Select
            label="Preview audience"
            useReactSelect
            isSearchable
            options={plan.map(({ audience, changeCount }) => ({
              value: audience._id,
              label: `${changeCount > 0 ? '● ' : ''}${audience.name}`,
            }))}
            value={
              previewItem && {
                value: previewItem.audience._id,
                label: previewItem.audience.name,
              }
            }
            onChange={(option) => setPreviewId(option?.value ?? null)}
          />
          <If condition={previewItem}>
            <AudienceDiff
              audience={previewItem.audience}
              changes={previewItem.changes}
              href={collection.audienceUrl(previewItem.audience._id)}
            />
          </If>
        </section>

        <div className="bulk-edit__stage-actions">
          <Button theme={BUTTON_THEME.SECONDARY} onClick={onBack}>
            Back to Query
          </Button>
          <Button onClick={onContinue} disabled={affected.length === 0}>
            Review Changes ({affected.length})
          </Button>
        </div>
      </aside>
    </div>
  )
}

export default EditStage
